// src/components/LandingPage.jsx
import React, { useState, useEffect, useRef } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import './LandingPage.css';
import ScrollToTopLink from './ScrollRestoration';
import golfpic from '../Assets/Images/golf-aerial.jpg';
import medinahGif from '../Assets/Images/coursedetailslarge.gif';
import mapGif from '../Assets/Images/interactivemaplarge.gif';
import axios from 'axios';

// ----------------- JSON-LD (Structured Data) -----------------
const orgJsonLd = {
  '@context': 'https://schema.org',
  '@type': 'Organization',
  name: 'PrivateIQ',
  url: 'https://privateiq.golf/',
  logo: 'https://privateiq.golf/logo192.png', // update to your real logo path
  sameAs: [
    // add profiles you actually use; safe to leave empty or add later
    // 'https://www.linkedin.com/company/privateiq',
    // 'https://x.com/yourhandle'
  ]
};

const websiteJsonLd = {
  '@context': 'https://schema.org',
  '@type': 'WebSite',
  name: 'PrivateIQ',
  url: 'https://privateiq.golf/',
  potentialAction: {
    '@type': 'SearchAction',
    target: 'https://privateiq.golf/search?q={query}', // update if/when you have site search
    'query-input': 'required name=query'
  }
};

// ----------------- Testimonials -----------------
const testimonials = [
  { text: "PrivateIQ feels like the secret club for private clubs—I'm almost annoyed I didn't think of it first.", name: "Sarah W." },
  { text: "Finally, I don't have to play detective just to figure out initiation fees—thank you, PrivateIQ!", name: "Mike R." },
  { text: "Who knew comparing clubs and chatting with similar golfers could be this addictive?", name: "Emily K." },
  { text: "This site is like Yelp for golf clubs, but without the nonsense reviews from people who don't actually play golf.", name: "Jake P." },
  { text: "It's like PrivateIQ crawled into my brain and figured out exactly what I needed as a golfer—spooky, but I'll take it.", name: "Anna L." },
  { text: "I'm not sure what I love more: the ridiculously detailed data or the fact that I can actually talk to others about it.", name: "Chris M." },
  { text: "It's rare to find a platform this sleek that also has real substance—usually you only get one or the other.", name: "Laura T." },
  { text: "The forums are surprisingly drama-free, which, for golfers, is saying something.", name: "Tom B." },
  { text: "I came for the data, stayed for the community, and now I'm basically running my own clubhouse here.", name: "Jenny L." },
  { text: "PrivateIQ makes the private club world feel a little less…private.", name: "Sam D." },
  { text: "It's like having an insider golf concierge, but without the awkward small talk.", name: "Brian F." },
  { text: "I didn't know I needed this until I joined—and now I can't shut up about it.", name: "Kelly P." },
  { text: "PrivateIQ solved a problem I didn't realize I had—turns out, I've been blindly guessing about clubs my whole life.", name: "Andrew H." },
  { text: "This platform is a cheat code for navigating the private golf world—don't tell too many people, though.", name: "Maggie S." },
  { text: "Who knew comparing clubs and avoiding bad decisions could actually be fun?", name: "Chris J." },
  { text: "It's basically a clubhouse for my phone—except this one actually has good Wi-Fi.", name: "Alex C." },
  { text: "Finally, a place where I can get real answers about clubs without digging through 20-year-old forum threads.", name: "Sophia G." },
  { text: "I've made better connections here in a week than I have in years at my actual club.", name: "Nathan O." },
  { text: "PrivateIQ gives me the information on clubs I never thought I'd have access to—it's like having a backstage pass to the golf world.", name: "Zoe P." },
];

const LandingPage = () => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const demoSectionRef = useRef(null);
  const location = useLocation();

  // Promo code modal state
  const [showPromoModal, setShowPromoModal] = useState(false);
  const [promoData, setPromoData] = useState(null);
  const [copied, setCopied] = useState(false);

  // Testimonial navigation
  const nextTestimonial = () => {
    setCurrentIndex((prevIndex) => (prevIndex + 1) % testimonials.length);
  };
  const prevTestimonial = () => {
    setCurrentIndex((prevIndex) => (prevIndex - 1 + testimonials.length) % testimonials.length);
  };

  // Check for influencer referral code in URL
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const refSlug = params.get('ref');

    if (refSlug) {
      // Fetch promo code details from API
      axios.get(`https://lionfish-app-w73zu.ondigitalocean.app/api/promo-code-by-ref/${refSlug}/`)
        .then(response => {
          const data = response.data;
          setPromoData(data);
          setShowPromoModal(true);

          // Store promo code in localStorage for checkout page
          localStorage.setItem('influencer_promo_code', data.code);
          localStorage.setItem('influencer_name', data.influencer_name || '');
        })
        .catch(error => {
          console.error('Error fetching promo code:', error);
        });
    }
  }, [location]);

  // Copy promo code to clipboard
  const copyPromoCode = () => {
    if (promoData?.code) {
      navigator.clipboard.writeText(promoData.code).then(() => {
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
      }).catch(err => {
        console.error('Failed to copy:', err);
      });
    }
  };

  // Close modal
  const closeModal = () => {
    setShowPromoModal(false);
  };

  // Preload GIFs
  useEffect(() => {
    [medinahGif, mapGif].forEach((src) => {
      const img = new Image();
      img.src = src;
    });
  }, []);

  // ----------------- SEO Strings -----------------
  const seoTitle = 'PrivateIQ – Private Golf Club Data & Insights';
  const seoDescription =
    'PrivateIQ is the source for private golf club data: initiation fees, dues, amenities, scorecards, and community insights across 2,500+ clubs nationwide.';

  return (
    <div className="landing-page">
      {/* ----------------- SEO / Social Meta ----------------- */}
      <Helmet>
        <title>{seoTitle}</title>
        <meta name="description" content={seoDescription} />
        <link rel="canonical" href="https://privateiq.golf/" />

        {/* Basic brand signals */}
        <meta name="application-name" content="PrivateIQ" />
        <meta name="theme-color" content="#004d40" />

        {/* Open Graph */}
        <meta property="og:type" content="website" />
        <meta property="og:site_name" content="PrivateIQ" />
        <meta property="og:title" content={seoTitle} />
        <meta property="og:description" content={seoDescription} />
        <meta property="og:url" content="https://privateiq.golf/" />
        <meta property="og:image" content="https://privateiq.golf/og-cover.jpg" />{/* update path */}

        {/* Twitter Card */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={seoTitle} />
        <meta name="twitter:description" content={seoDescription} />
        <meta name="twitter:image" content="https://privateiq.golf/og-cover.jpg" />{/* update path */}

        {/* JSON-LD */}
        <script type="application/ld+json">{JSON.stringify(orgJsonLd)}</script>
        <script type="application/ld+json">{JSON.stringify(websiteJsonLd)}</script>
      </Helmet>

      {/* ----------------- Hero Section ----------------- */}
      <section className="hero-section" aria-label="Hero">
        <div className="hero-content text-center">
          {/* Brand-forward H1 helps you win the 'privateiq' query */}
          <h1 className="visually-hidden">
            PrivateIQ — Private Golf Club Data &amp; Insights
          </h1>

          <h2 className="hero-title">
            All The Data
            <br />
            All The Clubs
            <br />
            All in One Place
          </h2>

          <p className="hero-subtitle">Explore and compare 2,500+ private clubs.</p>
          <Link to="/register" className="cta-btn">Get Started</Link>
        </div>

        {/* Optional decorative image (kept for LCP if styled properly) */}
      </section>

      {/* ----------------- Features Section ----------------- */}
      <section id="features" className="features-section" aria-labelledby="features-title">
        <div className="container">
          <h2 id="features-title" className="section-title text-center">What We Offer</h2>
          <div className="features-grid">
            <FeatureCard title="Find Private Clubs" description="Discover private clubs near you and across the country." icon="🔍" />
            <FeatureCard title="Membership Insights" description="Get detailed insights into membership fees, amenities, and more." icon="💡" />
            <FeatureCard title="Community" description="Interact with like-minded golfers (or opt to stay 100% anonymous)." icon="🤝" />
            <FeatureCard title="Potential Membership" description="Users can opt to exchange information with growing clubs we partner with." icon="🏌️" />
            <FeatureCard title="Revision Rewards" description="We distribute $1,000+ quarterly among our top contributors for submitting updates." icon="💸" />
            <FeatureCard title="Privacy" description="Your privacy is our priority. We never share your info without explicit consent." icon="🔒" />
          </div>
        </div>
      </section>

      {/* ----------------- Demos Section ----------------- */}
      <section id="demos" className="demos-section py-16 bg-gray-50" ref={demoSectionRef} aria-labelledby="demos-title">
        <div className="container mx-auto px-6 text-center">
          <h2 id="demos-title" className="section-title">Master the Evolving Golf Market.</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            <div className="demo-card">
              <h3 className="text-xl font-semibold text-gray-800 mt-4">Robust Club Details</h3>
              <hr className="section-divider" />
              <hr className="divider" />
              <div className="demo-gif-container">
                <img
                  src={medinahGif}
                  alt="Animated walkthrough of a club details page in PrivateIQ"
                  className="demo-gif"
                  loading="eager"
                  decoding="async"
                />
              </div>
            </div>
            <div className="demo-card">
              <h3 className="text-xl font-semibold text-gray-800 mt-4">Interactive Club Map</h3>
              <hr className="section-divider" />
              <hr className="divider" />
              <div className="demo-gif-container">
                <img
                  src={mapGif}
                  alt="Animated interactive map of US private golf clubs"
                  className="demo-gif"
                  loading="eager"
                  decoding="async"
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ----------------- How It Works Section ----------------- */}
      <section className="how-it-works-section" aria-labelledby="how-title">
        <div className="container">
          <h2 id="how-title" className="section-title">How it Works</h2>
          <div className="how-it-works-grid">
            <HowItWorksCard
              step="1"
              title="Create Your Account"
              description={
                <>
                  When you register, you'll be asked for a few key details like household income,
                  marital status, and family size.
                  <hr className="landingpage-divider" />
                  <em>Your personal contact information—like your name, email, and phone number—is never shared without your approval.</em>
                </>
              }
            />
            <HowItWorksCard
              step="2"
              title="Anonymous Insights"
              description={
                <>
                  We share anonymous profiles with clubs to help them understand potential members in their area.
                  <hr className="landingpage-divider" />
                  <em>These profiles ensure your privacy by never revealing personal information.</em>
                </>
              }
            />
            <HowItWorksCard
              step="3"
              title="Stay in Control"
              description={
                <>
                  If a club expresses interest in your profile, we notify you.
                  <hr className="landingpage-divider" />
                  <em>If you approve sharing your contact information with the club and are invited to tour, they are required to let you play the course once.</em>
                </>
              }
            />
          </div>
        </div>

        {/* Transparency Section */}
        <section className="transparency-section" aria-label="Transparency and timing">
          <div className="transparency-card">
            <h3 className="transparency-header">Transparency + Timing</h3>
            <p className="transparency-quote">
              <em>Pioneering innovative opportunities for clubs to interact with potential members is at the core of our long-term strategy.</em>
            </p>
            <hr className="landingpage-divider" />
            <p className="transparency-details">
              While we're actively building our user base to attract club partnerships, this feature will
              take shape as we onboard clubs.
            </p>
          </div>
        </section>
      </section>

      {/* ----------------- Testimonials ----------------- */}
      <section id="testimonials" className="testimonials-section" aria-labelledby="testimonials-title">
        <div className="container text-center">
          <h2 id="testimonials-title" className="section-title">What Our Members Are Saying</h2>
          <div className="testimonial-carousel" role="region" aria-label="Testimonials carousel">
            <button onClick={prevTestimonial} className="carousel-btn prev-btn" aria-label="Previous testimonial">❮</button>
            <div className="testimonial-card">
              <p className="testimonial-text">"{testimonials[currentIndex].text}"</p>
              <p className="testimonial-name">- {testimonials[currentIndex].name}</p>
            </div>
            <button onClick={nextTestimonial} className="carousel-btn next-btn" aria-label="Next testimonial">❯</button>
          </div>
        </div>
      </section>

      {/* ----------------- Pricing ----------------- */}
      <section id="pricing" className="pricing-section" aria-labelledby="pricing-title">
        <div className="container">
          <h2 id="pricing-title" className="section-title text-center">Pricing</h2>
          <div className="pricing-grid">
            <PricingCard
              title="Annual Membership"
              features={['Zero advertisements', 'Cancel anytime']}
            />
          </div>
        </div>
      </section>

      {/* ----------------- Influencer Promo Modal ----------------- */}
      {showPromoModal && promoData && (
        <div className="promo-modal-overlay" onClick={closeModal}>
          <div className="promo-modal-content" onClick={(e) => e.stopPropagation()}>
            <button className="promo-modal-close" onClick={closeModal} aria-label="Close modal">
              ×
            </button>
            <div className="promo-modal-body">
              <h2 className="promo-modal-title">
                {promoData.influencer_name && `${promoData.influencer_name} sent you here!`}
              </h2>
              <p className="promo-modal-subtitle">
                Get {promoData.discount_percent}% off your PrivateIQ membership
              </p>
              <div className="promo-code-display">
                <code className="promo-code-text">{promoData.code}</code>
              </div>
              <button
                className="promo-copy-btn"
                onClick={copyPromoCode}
                disabled={copied}
              >
                {copied ? 'Copied!' : 'Copy Code'}
              </button>
              {promoData.description && (
                <p className="promo-modal-description">{promoData.description}</p>
              )}
              <p className="promo-modal-info">
                Apply code at checkout to receive discount.
              </p>
            </div>
          </div>
        </div>
      )}

      {/* ----------------- Footer ----------------- */}
      <footer className="footer">
        <div className="container text-center">
          <p>
            &copy; 2025 Non-Target LLC. All rights reserved. |{' '}
            <Link to="/termsofservice">Terms of Service</Link> |{' '}
            <Link to="/privacypolicy">Privacy Policy</Link>
          </p>
        </div>
      </footer>
    </div>
  );
};

// ----------------- Reusable Subcomponents -----------------
const HowItWorksCard = ({ step, title, description }) => (
  <div className="how-it-works-card">
    <div className="step-number">{step}</div>
    <h3 className="how-it-works-title">{title}</h3>
    <p className="how-it-works-description">{description}</p>
  </div>
);

// Expanded-only FeatureCard
const FeatureCard = ({ title, description, icon }) => {
  const [expanded, setExpanded] = useState(false);
  const handleExpand = () => {
    if (!expanded) setExpanded(true);
  };
  return (
    <div
      className={`feature-card ${expanded ? 'expanded' : ''}`}
      onClick={handleExpand}
      style={{ cursor: expanded ? 'default' : 'pointer' }}
      role="button"
      aria-expanded={expanded}
      tabIndex={0}
      onKeyDown={(e) => { if ((e.key === 'Enter' || e.key === ' ') && !expanded) setExpanded(true); }}
    >
      <div className="feature-header">
        <div className="icon" aria-hidden="true">{icon}</div>
        <h3 className="feature-title">{title}</h3>
        {!expanded && <span className="expand-icon" aria-hidden="true">+</span>}
      </div>
      <div className="feature-details">
        <p className="feature-description">{description}</p>
      </div>
    </div>
  );
};

const PricingCard = ({ title, features }) => (
  <div className="pricing-card" itemScope itemType="https://schema.org/Offer">
    <h3 className="pricing-title" itemProp="name">{title}</h3>
    <div className="plan-price">
      $20 <small>/year</small>
    </div>
    <ul className="pricing-features">
      {features.map((feature, index) => (
        <li key={index} className="pricing-feature-item">{feature}</li>
      ))}
    </ul>
    <ScrollToTopLink to="/register" className="cta-btn">
      Join Now
    </ScrollToTopLink>
  </div>
);

export default LandingPage;
